import type { TFunction } from 'i18next';
import React from 'react';
import {
  getFieldValueOptions,
  getInterpretationRuleFieldSelectOptions,
} from './interpretation-rule-field-options';
import {
  DescribeMentionGroup,
  DescribeMentionItem,
  DescribeMentionList,
  DescribeMentionPanel,
  DescribeMentionWrap,
  RuleDescribeTextArea,
} from './extraction-rules-styles';
import type {
  InterpretationRuleField,
  InterpretationRuleFieldOptions,
} from './types';

type MentionMode = 'field' | 'value';

interface MentionState {
  mode: MentionMode;
  query: string;
  triggerIndex: number;
  endIndex: number;
  field?: InterpretationRuleField;
}

interface CaretPosition {
  top: number;
  left: number;
}

export interface DescribeFieldMentionsProps {
  id?: string;
  value: string;
  onChange: (value: string) => void;
  fieldOptions: InterpretationRuleFieldOptions;
  t: TFunction;
  textareaRef?: React.RefObject<HTMLTextAreaElement | null>;
  placeholder?: string;
}

function normalizeQuery(value: string): string {
  return value.trim().toLowerCase();
}

function resolveTextareaElement(node: unknown): HTMLTextAreaElement | null {
  if (!node) return null;
  if (node instanceof HTMLTextAreaElement) return node;
  const candidate = node as {
    resizableTextArea?: { textArea?: HTMLTextAreaElement | null };
    textArea?: HTMLTextAreaElement | null;
  };
  return candidate.resizableTextArea?.textArea ?? candidate.textArea ?? null;
}

/** Mirror-div technique to position the menu at the text cursor. */
function getCaretCoordinates(
  textarea: HTMLTextAreaElement,
  position: number,
  container: HTMLElement
): CaretPosition {
  const style = window.getComputedStyle(textarea);
  const mirror = document.createElement('div');
  const properties = [
    'fontFamily',
    'fontSize',
    'fontWeight',
    'fontStyle',
    'letterSpacing',
    'textTransform',
    'wordSpacing',
    'textIndent',
    'whiteSpace',
    'wordWrap',
    'lineHeight',
    'paddingTop',
    'paddingRight',
    'paddingBottom',
    'paddingLeft',
    'borderTopWidth',
    'borderRightWidth',
    'borderBottomWidth',
    'borderLeftWidth',
    'boxSizing',
  ] as const;

  mirror.style.position = 'absolute';
  mirror.style.visibility = 'hidden';
  mirror.style.whiteSpace = 'pre-wrap';
  mirror.style.wordWrap = 'break-word';
  mirror.style.overflow = 'hidden';
  mirror.style.width = style.width;
  mirror.style.top = '0';
  mirror.style.left = '-9999px';

  for (const property of properties) {
    mirror.style[property] = style[property];
  }

  const textBefore = textarea.value.slice(0, position);
  const textAfter = textarea.value.slice(position);
  mirror.textContent = textBefore;
  const marker = document.createElement('span');
  marker.textContent = textAfter.length > 0 ? textAfter[0] : '.';
  mirror.appendChild(marker);
  document.body.appendChild(mirror);

  const markerRect = marker.getBoundingClientRect();
  const mirrorRect = mirror.getBoundingClientRect();
  const textareaRect = textarea.getBoundingClientRect();
  const containerRect = container.getBoundingClientRect();

  document.body.removeChild(mirror);

  const relativeTop = markerRect.top - mirrorRect.top;
  const relativeLeft = markerRect.left - mirrorRect.left;

  return {
    top:
      textareaRect.top -
      containerRect.top +
      relativeTop -
      textarea.scrollTop,
    left:
      textareaRect.left -
      containerRect.left +
      relativeLeft -
      textarea.scrollLeft,
  };
}

export const DescribeFieldMentions: React.FC<DescribeFieldMentionsProps> = ({
  id,
  value,
  onChange,
  fieldOptions,
  t,
  textareaRef: externalTextareaRef,
  placeholder,
}) => {
  const internalTextareaRef = React.useRef<HTMLTextAreaElement | null>(null);
  const wrapRef = React.useRef<HTMLDivElement | null>(null);
  const nativeTextareaRef = React.useRef<HTMLTextAreaElement | null>(null);
  const textareaRef = externalTextareaRef ?? internalTextareaRef;
  const pendingFieldRef = React.useRef<InterpretationRuleField | null>(null);
  const [mention, setMention] = React.useState<MentionState | null>(null);
  const [panelPosition, setPanelPosition] = React.useState<CaretPosition | null>(
    null
  );
  const [highlightedIndex, setHighlightedIndex] = React.useState(0);
  const itemRefs = React.useRef<HTMLButtonElement[]>([]);

  const fieldEntries = React.useMemo(() => {
    return getInterpretationRuleFieldSelectOptions(t, fieldOptions).flatMap(
      (group) =>
        group.options.map((option) => ({
          group: group.label,
          field: option.value,
          label: option.label,
          searchText: option.searchText,
        }))
    );
  }, [fieldOptions, t]);

  const bindTextareaRef = React.useCallback(
    (node: unknown) => {
      const textarea = resolveTextareaElement(node);
      nativeTextareaRef.current = textarea;
      if (textareaRef && 'current' in textareaRef) {
        textareaRef.current = textarea;
      }
    },
    [textareaRef]
  );

  const closeMention = React.useCallback(() => {
    setMention(null);
    setPanelPosition(null);
    setHighlightedIndex(0);
  }, []);

  const updatePanelPosition = React.useCallback(
    (cursor: number, textareaOverride?: HTMLTextAreaElement | null) => {
      const textarea = textareaOverride ?? nativeTextareaRef.current;
      const container = wrapRef.current;
      if (!textarea || !container) return;
      try {
        setPanelPosition(getCaretCoordinates(textarea, cursor, container));
      } catch {
        setPanelPosition(null);
      }
    },
    []
  );

  const applyTextEdit = React.useCallback(
    (
      nextValue: string,
      cursor: number,
      nextMention?: MentionState | null
    ) => {
      onChange(nextValue);
      setMention(nextMention ?? null);

      window.requestAnimationFrame(() => {
        const textarea = nativeTextareaRef.current;
        if (!textarea) return;
        textarea.focus();
        textarea.setSelectionRange(cursor, cursor);
        if (nextMention) {
          updatePanelPosition(cursor, textarea);
        } else {
          setPanelPosition(null);
        }
      });
    },
    [onChange, updatePanelPosition]
  );

  const replaceActiveMention = React.useCallback(
    (replacement: string) => {
      if (!mention) return;
      const before = value.slice(0, mention.triggerIndex);
      const after = value.slice(mention.endIndex);
      const nextValue = `${before}${replacement}${after}`;
      const cursor = before.length + replacement.length;
      applyTextEdit(nextValue, cursor, null);
    },
    [applyTextEdit, mention, value]
  );

  const detectMention = React.useCallback(
    (text: string, cursor: number, textarea?: HTMLTextAreaElement | null) => {
      const prefix = text.slice(0, cursor);
      const fieldMatch = prefix.match(/@([^\s#@]*)$/);
      if (fieldMatch && fieldMatch.index != null) {
        const nextMention: MentionState = {
          mode: 'field',
          query: fieldMatch[1] ?? '',
          triggerIndex: fieldMatch.index,
          endIndex: cursor,
        };
        setMention(nextMention);
        updatePanelPosition(cursor, textarea);
        return;
      }

      const valueMatch = prefix.match(/#([^\s#@]*)$/);
      if (valueMatch && valueMatch.index != null && pendingFieldRef.current) {
        const nextMention: MentionState = {
          mode: 'value',
          query: valueMatch[1] ?? '',
          triggerIndex: valueMatch.index,
          endIndex: cursor,
          field: pendingFieldRef.current,
        };
        setMention(nextMention);
        updatePanelPosition(cursor, textarea);
        return;
      }

      closeMention();
    },
    [closeMention, updatePanelPosition]
  );

  const handleChange = (event: React.ChangeEvent<HTMLTextAreaElement>) => {
    const textarea = event.currentTarget;
    nativeTextareaRef.current = textarea;
    const nextValue = textarea.value;
    const cursor = textarea.selectionStart ?? nextValue.length;
    onChange(nextValue);
    detectMention(nextValue, cursor, textarea);
  };

  const handleKeyUp = (event: React.KeyboardEvent<HTMLTextAreaElement>) => {
    const textarea = event.currentTarget;
    nativeTextareaRef.current = textarea;
    detectMention(
      textarea.value,
      textarea.selectionStart ?? textarea.value.length,
      textarea
    );
  };

  const handleSelect = (event: React.SyntheticEvent<HTMLTextAreaElement>) => {
    const textarea = event.currentTarget;
    nativeTextareaRef.current = textarea;
    detectMention(
      textarea.value,
      textarea.selectionStart ?? textarea.value.length,
      textarea
    );
  };

  const handleSelectField = (field: InterpretationRuleField, label: string) => {
    if (!mention) return;

    pendingFieldRef.current = field;
    const valueOptions = getFieldValueOptions(field, fieldOptions);
    const before = value.slice(0, mention.triggerIndex);
    const after = value.slice(mention.endIndex);

    if (valueOptions?.length) {
      const token = `@${label}: #`;
      const nextValue = `${before}${token}${after}`;
      const cursor = before.length + token.length;
      const hashIndex = before.length + `@${label}: `.length;
      applyTextEdit(nextValue, cursor, {
        mode: 'value',
        query: '',
        triggerIndex: hashIndex,
        endIndex: cursor,
        field,
      });
      return;
    }

    replaceActiveMention(`@${label} `);
  };

  const handleSelectValue = (label: string) => {
    replaceActiveMention(`#${label} `);
  };

  const filteredFieldItems = React.useMemo(() => {
    if (!mention || mention.mode !== 'field') return [];
    const query = normalizeQuery(mention.query);
    return fieldEntries.filter((entry) => {
      if (!query) return true;
      return (
        entry.label.toLowerCase().includes(query) ||
        entry.searchText.includes(query)
      );
    });
  }, [fieldEntries, mention]);

  const filteredValueItems = React.useMemo(() => {
    if (!mention || mention.mode !== 'value' || !mention.field) return [];
    const options = getFieldValueOptions(mention.field, fieldOptions) ?? [];
    const query = normalizeQuery(mention.query);
    return options.filter((option) => {
      if (!query) return true;
      return (
        option.label.toLowerCase().includes(query) ||
        String(option.value).toLowerCase().includes(query)
      );
    });
  }, [fieldOptions, mention]);

  const groupedFieldItems = React.useMemo(() => {
    const groups = new Map<string, typeof filteredFieldItems>();
    for (const item of filteredFieldItems) {
      const list = groups.get(item.group) ?? [];
      list.push(item);
      groups.set(item.group, list);
    }
    return [...groups.entries()];
  }, [filteredFieldItems]);

  type ActiveListItem =
    | { kind: 'field'; field: InterpretationRuleField; label: string }
    | { kind: 'value'; label: string };

  const activeListItems = React.useMemo((): ActiveListItem[] => {
    if (!mention) return [];
    if (mention.mode === 'field') {
      return filteredFieldItems.map((item) => ({
        kind: 'field' as const,
        field: item.field,
        label: item.label,
      }));
    }
    return filteredValueItems.map((option) => ({
      kind: 'value' as const,
      label: option.label,
    }));
  }, [filteredFieldItems, filteredValueItems, mention]);

  const handleKeyDown = (event: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (event.key === 'Escape' && mention) {
      event.preventDefault();
      event.stopPropagation();
      closeMention();
      return;
    }

    if (!mention || activeListItems.length === 0) return;

    if (event.key === 'ArrowDown') {
      event.preventDefault();
      setHighlightedIndex(
        (index) => (index + 1) % activeListItems.length
      );
      return;
    }

    if (event.key === 'ArrowUp') {
      event.preventDefault();
      setHighlightedIndex(
        (index) =>
          (index - 1 + activeListItems.length) % activeListItems.length
      );
      return;
    }

    if (event.key === 'Enter') {
      event.preventDefault();
      const item = activeListItems[highlightedIndex];
      if (!item) return;
      if (item.kind === 'field') {
        handleSelectField(item.field, item.label);
      } else {
        handleSelectValue(item.label);
      }
    }
  };

  const showMentionPanel =
    mention &&
    ((mention.mode === 'field' && filteredFieldItems.length > 0) ||
      (mention.mode === 'value' && filteredValueItems.length > 0));

  React.useLayoutEffect(() => {
    if (!mention) return;
    updatePanelPosition(mention.endIndex);
  }, [mention, updatePanelPosition, value]);

  React.useEffect(() => {
    setHighlightedIndex(0);
  }, [mention?.query, mention?.mode, mention?.field]);

  React.useEffect(() => {
    setHighlightedIndex((index) =>
      activeListItems.length === 0
        ? 0
        : Math.min(index, activeListItems.length - 1)
    );
    itemRefs.current = itemRefs.current.slice(0, activeListItems.length);
  }, [activeListItems.length]);

  React.useLayoutEffect(() => {
    if (!showMentionPanel) return;
    itemRefs.current[highlightedIndex]?.scrollIntoView({ block: 'nearest' });
  }, [highlightedIndex, showMentionPanel]);

  let fieldItemIndex = 0;

  return (
    <DescribeMentionWrap ref={wrapRef}>
      <RuleDescribeTextArea
        id={id}
        ref={bindTextareaRef}
        value={value}
        onChange={handleChange}
        onKeyDown={handleKeyDown}
        onKeyUp={handleKeyUp}
        onSelect={handleSelect}
        onClick={handleSelect}
        onScroll={() => {
          const textarea = nativeTextareaRef.current;
          if (!textarea || !mention) return;
          updatePanelPosition(
            textarea.selectionStart ?? mention.endIndex,
            textarea
          );
        }}
        onBlur={() => window.setTimeout(closeMention, 120)}
        placeholder={placeholder}
      />
      {showMentionPanel ? (
        <DescribeMentionPanel
          style={
            panelPosition
              ? {
                  top: panelPosition.top + 22,
                  left: Math.max(0, panelPosition.left),
                  right: 'auto',
                  width: 'min(280px, calc(100% - 16px))',
                }
              : {
                  top: 'calc(100% + 4px)',
                  left: 0,
                  right: 0,
                }
          }
        >
          <DescribeMentionList>
            {mention.mode === 'field'
              ? groupedFieldItems.map(([group, items]) => (
                  <DescribeMentionGroup key={group}>
                    <div>{group}</div>
                    {items.map((item) => {
                      const currentIndex = fieldItemIndex++;
                      return (
                        <DescribeMentionItem
                          key={item.field}
                          type="button"
                          $active={currentIndex === highlightedIndex}
                          ref={(element) => {
                            if (element) {
                              itemRefs.current[currentIndex] = element;
                            }
                          }}
                          onMouseDown={(event) => event.preventDefault()}
                          onMouseEnter={() => setHighlightedIndex(currentIndex)}
                          onClick={() =>
                            handleSelectField(item.field, item.label)
                          }
                        >
                          {item.label}
                        </DescribeMentionItem>
                      );
                    })}
                  </DescribeMentionGroup>
                ))
              : filteredValueItems.map((option, index) => (
                  <DescribeMentionItem
                    key={`${mention.field}-${option.value}`}
                    type="button"
                    $active={index === highlightedIndex}
                    ref={(element) => {
                      if (element) {
                        itemRefs.current[index] = element;
                      }
                    }}
                    onMouseDown={(event) => event.preventDefault()}
                    onMouseEnter={() => setHighlightedIndex(index)}
                    onClick={() => handleSelectValue(option.label)}
                  >
                    {option.label}
                  </DescribeMentionItem>
                ))}
          </DescribeMentionList>
        </DescribeMentionPanel>
      ) : null}
    </DescribeMentionWrap>
  );
};
